import React from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: `Rep Fleet ("we," "us," "our") provides fleet maintenance software as a service to trucking companies and their staff (fleet admins, dispatchers, mechanics, and drivers). This Privacy Policy explains what personal information we collect when you use https://rep-fleet.com or any Rep Fleet mobile app, why we collect it, and how we protect it.`,
  },
  {
    title: "2. What we collect",
    body: `Account information you provide: name, email address, phone number, role (admin, mechanic, driver, etc.), and the company you belong to.

Operational data you enter: trucks, trailers, drivers, repairs, services, fuel entries, invoices, parts, and photos. This content is owned by your company.

Automatically collected: device model, operating system, IP address, timestamps, feature usage, and crash diagnostics. Used to keep the service running and to detect abuse.

Files you upload: repair invoices, work orders, fuel statements, and photos. These are stored securely and only accessible to authorized members of your company.

Location data (optional): if your company connects a Motive telematics account, we receive truck GPS positions and mileage. No GPS data is collected without an admin explicitly configuring the integration.`,
  },
  {
    title: "3. What we do NOT collect",
    body: `We do not sell your data. We do not share it with advertisers. We do not build advertising profiles from it. We do not access your data for any purpose other than operating the service, providing support, and complying with law.`,
  },
  {
    title: "4. How we use your data",
    body: `To operate the platform: authenticate users, save your fleet records, deliver notifications, and route messages between drivers and mechanics.

To process invoices with AI: PDF invoices you upload are sent to our AI parsing provider (Google Gemini via the Emergent LLM gateway) so that parts, costs, and shop details can be extracted for your review. The AI provider processes the file to return structured data; we do not retain the raw file with them beyond the parse call, and they are contractually prohibited from using your content to train their models.

To provide support: our team may access your account with your permission to help troubleshoot.

To improve the product: aggregate, de-identified usage patterns help us decide what to build next.

To comply with law and protect our users from fraud or abuse.`,
  },
  {
    title: "5. Sub-processors",
    body: `We rely on the following sub-processors:

• MongoDB Atlas — database storage (US region).
• Google Gemini (via Emergent LLM Key) — AI PDF extraction and chat assistant.
• Motive — optional telematics integration; only used if your admin connects a Motive account.
• Cloud infrastructure — for compute, storage, and content delivery.

We use industry-standard encryption in transit (TLS) and at rest.`,
  },
  {
    title: "6. Retention",
    body: `We retain your account data for as long as your company is an active customer. If your company cancels, we retain records for up to 90 days to allow account restoration, then purge unless required by law to keep them longer. Individual users can request their personal data be exported or deleted at any time by writing to support@rep-fleet.com.`,
  },
  {
    title: "7. Your rights",
    body: `Depending on where you live, you may have the right to access, correct, port, or delete your personal data, and to object to or restrict certain processing. To exercise any of these rights, email support@rep-fleet.com. We respond within 30 days.

Note: because Rep Fleet is a business tool, most account and repair records belong to the company (not the individual user). Some deletion requests must be routed through your company admin.`,
  },
  {
    title: "8. Children's privacy",
    body: `Rep Fleet is a workplace tool intended for adults 18 and older. We do not knowingly collect personal information from anyone under 18.`,
  },
  {
    title: "9. International transfers",
    body: `Rep Fleet is hosted in the United States. If you access the service from outside the US, you understand that your information will be transferred to and processed in the US, which may have different data protection laws than your jurisdiction.`,
  },
  {
    title: "10. Security",
    body: `Passwords are hashed with bcrypt. Sessions use short-lived JWTs. Tenant isolation means Company A cannot access Company B's data even via the API. We monitor for suspicious activity and patch security issues promptly. If we ever suffer a data breach that affects you, we will notify affected users without undue delay.`,
  },
  {
    title: "11. Changes to this policy",
    body: `We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page reflects the latest revision. Material changes will be announced in-app or by email at least 14 days before taking effect.`,
  },
  {
    title: "12. Contact",
    body: `Questions or requests? Write to support@rep-fleet.com. Physical mail can be sent to the address listed on https://rep-fleet.com.`,
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs font-mono uppercase tracking-widest text-blue-600 hover:text-blue-800" data-testid="privacy-back-home">← Back to Rep Fleet</Link>
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-blue-600 font-mono font-bold">/// Legal</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2 text-slate-900">Privacy Policy</h1>
          <p className="text-sm text-slate-500 font-mono mt-2">Last updated: February 16, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none mt-8 space-y-8">
          <p className="text-base leading-relaxed">
            Rep Fleet takes your privacy seriously. This policy is written in plain English so you actually understand what we do — and what we don&apos;t do — with the information you and your company entrust to us.
          </p>

          {SECTIONS.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i}`}>
              <h2 className="font-display text-xl font-black tracking-tight text-slate-900 mb-2">{s.title}</h2>
              <p className="text-base leading-relaxed whitespace-pre-wrap text-slate-700">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between text-xs font-mono text-slate-500">
          <Link to="/terms" className="hover:text-blue-600" data-testid="privacy-to-terms">Terms of Service →</Link>
          <span>© 2026 Rep Fleet</span>
        </div>
      </div>
    </div>
  );
}
